import { useMemo, useState, useEffect, useContext } from 'react'
import { useTheme } from '@mui/material/styles'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Tooltip, Button, TextField } from '@mui/material'

import _ from 'lodash'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import KeyOffIcon from '@mui/icons-material/KeyOff'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import KeyIcon from '@mui/icons-material/Key'

import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { getApiKeys, addApiKey, deleteApiKey, setApiKeyStatus, toggleApiKeyAdminAccess } from '../../providers/clients-api'

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './key-list-to-excel'
import { confirm } from '/components/confim-dialog'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const APIKeyAdmin = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])


  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState([])
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [keyName, setKeyName] = useState("")

  useEffect(() => {
    getUsers()
  }, [])

  const getUsers = async (args) => {
    setLoading(true)
    const u = await getApiKeys({ apiSource, apiKey,...args })
    setLoading(false)
    if (u.ok) {
      setUsers(u.keys)
    }
    // TODO add error handling
  }

  const addKey = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding new API key', variant: 'light' })
    const k = await addApiKey({ apiSource, apiKey, keyName })
    setProcessing(false)

    if (k.ok) {
      enqueueSnackbar({ message: 'API Key added', variant: 'success' })
      setUsers(k.keys)
      setKeyName("")
    } else {
      enqueueSnackbar({ message: k.error, variant: 'error' })
    }
  }

  const deleteKey = async keyId => {

    const confirmed = await confirm({
      message: 'Are you sure you want to delete this key rather than temporarily disable it?'
    })

    if (confirmed) {
      setProcessing(true)
      enqueueSnackbar({ message: 'Removing API Key', variant: 'light' })
      const k = await deleteApiKey({ apiSource, apiKey, keyId })

      setProcessing(false)

      if (k.ok) {
        enqueueSnackbar({ message: 'API Key removed', variant: 'success' })
        setUsers(k.keys)
      } else {
        enqueueSnackbar({ message: k.error, variant: 'error' })
      }
    }
  }

  const toggleKeyEnabled = async keyId => {

    const user = users.filter(u => u.user_id === keyId)[0]
    const status = 1 - user.user_enabled

    setProcessing(true)
    enqueueSnackbar({ message: 'Setting key status', variant: 'light' })
    const k = await setApiKeyStatus({ apiSource, apiKey, keyId, status })

    setProcessing(false)

    if (k.ok) {
      enqueueSnackbar({ message: 'Key status saved', variant: 'success' })
      setUsers(k.keys)
    } else {
      enqueueSnackbar({ message: k.error, variant: 'error' })
    }
  }

  const toggleAdmin = async keyId => {
    const u = await toggleApiKeyAdminAccess({ apiSource, apiKey, keyId })
    if (u.ok) {
      setUsers(u.keys)
    }
  }


  const columns = useMemo(() => [
    {
      accessorKey: 'user_id',
      header: 'Key ID',
      filterVariant: 'autocomplete',
      size: 80,
    },
    {
      accessorKey: 'username',
      header: 'Key name',
      filterVariant: 'autocomplete',
      size: 200,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
    {
      accessorKey: 'user_enabled',
      header: 'Enabled',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'client_admin_enabled',
      header: 'Admin Access',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },    
    {
      accessorKey: 'key',
      header: 'API Key',
      filterVariant: 'autocomplete',
      size: 200,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data: users,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.user_id,
    ...tableStyles,

    renderRowActionMenuItems: ({ closeMenu, row }) => getActions(toggleKeyEnabled, toggleAdmin, deleteKey, closeMenu, row),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-start gap-5'>
        <TextField value={keyName} onChange={e => setKeyName(e.target.value)} label="new key name" slotProps={{ inputLabel: { shrink: true } }} />
        <Button startIcon={<KeyIcon className='mr-2' />} onClick={addKey} disabled={keyName.length < 3} variant='contained'>Add Api Key</Button>
      </div>
    )
  })

  return (
    <>
      <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
          <MaterialReactTable table={table} />
        </StyledPaper>
      </Box>
      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="api_keys" data={users} excelExport={excelExport} />
    </>
  )
}

export default APIKeyAdmin


// Only allow view API Keys is user is a API only user.
// If user has studio access then they can log in to get their keys
// Scanner only users have keys, but never see them.
const getActions = (toggleKeyEnabled, toggleAdmin, deleteKey, closeMenu, row) => {
  const a = [
    <MenuItem key={3} onClick={() => { toggleKeyEnabled(row.id); closeMenu() }}><KeyIcon className='mr-2' />{row.original.user_enabled === 1 ? "Disable" : "Enable"} Key</MenuItem>,
    <MenuItem key={3} onClick={() => { toggleAdmin(row.id); closeMenu() }}><SupervisorAccountIcon className='mr-2' />{row.original.client_admin_enabled === 1 ? "Disable" : "Enable"} Admin Access for Key</MenuItem>,
    <MenuItem key={2} onClick={() => { deleteKey(row.id); closeMenu() }}><KeyOffIcon className='mr-2' /> Delete Key</MenuItem>,
  ]

  return a
}